/**
 * Adult ASD Semi-Structured Interview — Configuration
 *
 * Defines the clinical content for each DSM-5 ASD criterion assessed during
 * the autism assessment for adults. This mirrors the CYP ASD SSI config
 * structure but with a simpler, flatter layout.
 *
 * Probing questions — three supported shapes:
 *
 *   • Legacy (default): two flat arrays split by life stage:
 *       adulthoodProbingQuestions — prompts about current adult functioning
 *       childhoodProbingQuestions — retrospective prompts about childhood
 *     These are rendered as two collapsible accordion sections (Childhood /
 *     Adulthood) in AdultAsdCriterionPanel.
 *
 *   • Life-stage-agnostic flat list (organisation-specific):
 *       probingQuestions — single array of questions across the lifespan
 *     When this field is present the panel renders a single flat list and
 *     omits the Childhood / Adulthood accordions.
 *
 *   • Categorised accordions (organisation-specific):
 *       categorisedProbingQuestions — array of { category: string, questions: string[] }
 *     When this field is present the panel renders one collapsible accordion
 *     per category. Used when questions naturally cluster into named themes
 *     that the clinician should work through (e.g. Imaginative play, Managing
 *     change, Routines & rituals, Rigid thinking for B2).
 *
 *   The adulthoodProbingQuestions / childhoodProbingQuestions for criteria that
 *   have switched to a new shape are kept in code but commented out — they may
 *   be reactivated for other organisations.
 *
 * Currently only The Owl Centre is live with Adult Autism, and they prefer
 * life-stage-agnostic questions. Other organisations may prefer the split
 * layout — the accordion rendering code is preserved for that purpose.
 *
 * Functional impairment is captured within each criterion page (via the
 * per-criterion "Domains Impacted" chips) rather than in a separate panel.
 * There are 7 domains (one more than CYP): Social, Educational, Occupational,
 * Home, Emotional, Community, Self-care. "Occupational" is added because many
 * adult service users are in employment; "Educational" is kept because some
 * may still be in education.
 */

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/** Evidence rating options — identical to CYP ASD for consistency */
export const EVIDENCE_OPTIONS = [
  'Clear evidence',
  'Partial evidence',
  'No evidence',
  'Inconclusive',
];

// ---------------------------------------------------------------------------
// Criterion A1 — Social-emotional reciprocity
// ---------------------------------------------------------------------------

const CRITERION_A1 = {
  id: 'A1',
  criterionGroup: 'A',
  shortTitle: 'Social-emotional reciprocity',
  fullTitle: 'A1. Deficits in social-emotional reciprocity',
  description:
    'This criterion assesses the ability to initiate and sustain reciprocal social ' +
    'interaction, share emotional experiences, and respond to others in socially ' +
    'expected ways. It includes motivation to engage, intuitive responsiveness, ' +
    'and comfort with informal social dynamics.',
  available: true,

  // Categorised questions for The Owl Centre. Three thematic groups covering
  // conversational dynamics, emotional responses to social situations, and
  // initiating/responding to others.
  categorisedProbingQuestions: [
    {
      category: 'Conversation',
      questions: [
        'How do you feel about small talk?',
        'What about speaking on the phone?',
        'How do you find keeping a conversation going?',
        'Do you feel comfortable initiating conversation, or do you wait for others to initiate with you?',
        'What about if people are already having a conversation in a group or at an event — would you join in or not?',
        'Do you find communicating with others comes naturally to you, or do you have to script what you are going to say?',
        'Do you struggle to know when it\'s your turn to speak, or do you speak over people?',
        'How would you respond if someone interrupted you when you were speaking?',
        'Do you find that you tend to stay on topic or go off on a tangent when you\'re speaking?',
        'When you talk, is it easy for others to get their words in?',
        'Do others comment on how you talk — for example, that you say too much or too little?',
        'Is there any particular topic of conversation that you tend to talk about at length or steer the conversation towards?',
        'Did you or do you tend to take things literally?',
        'When someone else is talking, do you find it easy to read between the lines — if they say one thing and mean another?',
        'How are you with understanding jokes, or do you find you\'re usually the last to get them?',
        'How do you feel about receiving praise?',
        'If there was something you were pleased about, such as a promotion, would you want to tell others about it?',
      ],
    },
    {
      category: 'Emotional response to situations',
      questions: [
        'If something positive happened to you, like getting a new job or getting engaged, would you be excited to share this with others?',
        'What about when other people share their happy news with you — how would you feel or respond?',
        'How do you respond to praise from other people? What about as a child?',
        'How do you respond when someone is upset, ill, or crying? How do you think you responded as a child?',
        'Would you seek comfort if you were upset, ill, or crying? What about when you were a child?',
        'Do you enjoy getting hugs? What about as a child?',
        'Do you ever laugh or smile in situations that do not seem funny to most people?',
        'Have you ever not felt upset by something that others were, such as the death of a pet or relative?',
      ],
    },
    {
      category: 'Initiating and responding to others',
      questions: [
        'Do you feel like you behave the same with everyone — for example, talking the same way with a friend as with your boss — or do you behave differently with different people? What about when you were a child?',
        'Thinking back to when you were a child, if someone you didn\'t know came up to you, do you think you would have responded consistently? What about now?',
        'Do you share your feelings with other people or keep them to yourself? What about when you were a child?',
        'Does sharing your feelings feel natural to you?',
      ],
    },
  ],

  // Legacy life-stage-split questions — kept for potential reuse by other
  // organisations that prefer the Childhood / Adulthood accordion layout.
  // adulthoodProbingQuestions: [
  //   "How do you feel about mixing and socialising with other people?",
  //   "What's your experience with casual conversations like small talk or friendly chit-chat?",
  //   "If someone you're talking to becomes visibly upset like sad, angry, or distressed, what would your first instinct be?",
  // ],
  // childhoodProbingQuestions: [
  //   "When you were a child, what did you usually do during break times or free play?",
  //   "What are your memories of talking with other kids or adults when you were young? What did it feel like?",
  //   "When you were a child, how did you show or talk about things that excited you? For example, if you got a new toy or discovered something interesting, how did you share it with others?",
  // ],
};

// ---------------------------------------------------------------------------
// Criterion A2 — Nonverbal communication
// ---------------------------------------------------------------------------

const CRITERION_A2 = {
  id: 'A2',
  criterionGroup: 'A',
  shortTitle: 'Nonverbal communication',
  fullTitle: 'A2. Deficits in nonverbal communicative behaviours used for social interaction',
  description:
    'This criterion assesses the integration and use of nonverbal communication to ' +
    'regulate social interaction such as eye contact, facial expression, gestures, ' +
    'body posture, and prosody (the rhythm and tone of speech). These differences ' +
    'may affect how communication is interpreted by others, which can lead to ' +
    'misunderstandings or perceived social awkwardness.',
  available: true,

  // Categorised questions for The Owl Centre. Three thematic groups covering
  // eye contact and prosody, facial expression awareness, and gesture use.
  categorisedProbingQuestions: [
    {
      category: 'Eye contact & intonation',
      questions: [
        'How do you feel about making eye contact?',
        'Is it something you are consciously aware of or have to force yourself to do?',
        'What about as a child — how were you with eye contact then?',
        'Are you able to tell how someone is feeling from their tone of voice?',
        'Do you feel like you change your own tone of voice depending on how you\'re feeling?',
        'Is there anything different about the way you speak, e.g. flat intonation, unusual volume or pitch?',
      ],
    },
    {
      category: 'Facial expressions',
      questions: [
        'Do you feel like others can tell how you are feeling from your facial expressions?',
        'Do you feel like you are acutely aware of your facial expressions and having to control them to fit the situation?',
        'How are you at reading other people\'s expressions? Can you tell if someone is bored or upset? Do you sometimes think others are angry or sad when they\'re not?',
      ],
    },
    {
      category: 'Using and understanding gestures',
      questions: [
        'Do you use common gestures, such as blowing a kiss, clapping for a job well done, putting a finger to your lips to mean "be quiet", or shaking a finger for "bad"?',
        'Do you use gestures with your hands when you are trying to describe something?',
        'Do you use gestures with your hands to get attention, such as pointing at something?',
        'Do you use conventional gestures such as thumbs up or waving goodbye?',
        'Do you use conventional gestures such as "come here" (beckoning someone towards you) or "sit here" (patting the chair next to you)?',
      ],
    },
  ],

  // Legacy life-stage-split questions — kept for potential reuse by other
  // organisations that prefer the Childhood / Adulthood accordion layout.
  // adulthoodProbingQuestions: [
  //   "Some people use eye contact or gestures naturally, while others learn to do so over time. What's your experience been?",
  //   "How can you tell what someone is feeling from their face, tone of voice, or gestures?",
  //   "What does emotional connection or affection look like for you — whether through words, actions, or just being present?",
  // ],
  // childhoodProbingQuestions: [
  //   "What do you remember about making eye contact as a child?",
  //   "Thinking back, how did you read people's facial expressions and gestures when you were a child? Do you remember anyone commenting on this or giving you feedback?",
  //   "How did you show or respond to physical affection with family, friends, or peers?",
  //   "Thinking back, how did you know what you were feeling as a child?",
  // ],
};

// ---------------------------------------------------------------------------
// Criterion A3 — Relationships
// ---------------------------------------------------------------------------

const CRITERION_A3 = {
  id: 'A3',
  criterionGroup: 'A',
  shortTitle: 'Relationships',
  fullTitle: 'A3. Deficits in developing, maintaining, and understanding relationships',
  description:
    'This criterion captures difficulties in forming age-appropriate friendships, ' +
    'understanding social hierarchies, adjusting behaviour to different social ' +
    'contexts, and appreciating others\' perspectives. This may manifest as ' +
    'difficulty forming and sustaining relationships that are appropriate to the ' +
    'developmental level, from peer friendships to romantic partnerships.',
  available: true,

  // Categorised questions for The Owl Centre. Three thematic groups covering
  // friendship history, dyadic relationships/social rules, and group dynamics.
  categorisedProbingQuestions: [
    {
      category: 'Friendships',
      questions: [
        'Did you have friends growing up? What were your friendships like growing up?',
        'Tell me about the schools you were at — how did you get on with people there? Did you enjoy being with people? What about at work or college?',
        'Tell me how you get on with people — any problems in the past or now?',
        'Do you have close friends or a best friend? How did you meet? How long have you known each other?',
        'Did you spend time with your school friends outside of school, such as on play dates or sleepovers?',
        'Did you join any clubs or play out when you were a child with others in the neighbourhood?',
        'Do you find it easy to make friends?',
      ],
    },
    {
      category: 'Relationships',
      questions: [
        'Did you attend or have birthday parties as a child? What were they like for you?',
        'When you were a child did you enjoy playing with other children, or did you prefer playing by yourself or with your siblings?',
        'Would you behave differently with children you knew well versus children you hadn\'t seen before?',
        'Tell me what happens in informal social gatherings or parties.',
        'Do you often get told that what you\'ve said is impolite when you didn\'t mean for it to be?',
        'How has work gone — what kinds of jobs have you had, and why have you changed jobs?',
      ],
    },
    {
      category: 'Group play',
      questions: [
        'How do you feel in groups of friends?',
        'Did you prefer playing in a group, one-to-one, or alone?',
        'Did you like playing group games like sports or playground games as a child?',
        'How would you feel if someone broke the rules?',
        'Would you try to impose your own rules on games?',
        'How would you feel if you lost in a group game?',
      ],
    },
  ],

  // Legacy life-stage-split questions — kept for potential reuse by other
  // organisations that prefer the Childhood / Adulthood accordion layout.
  // adulthoodProbingQuestions: [
  //   "How do you form and maintain friendships or close relationships?",
  //   "How do you handle disagreements or misunderstandings with other people?",
  //   "How do you manage relationships in work, school, or community settings?",
  // ],
  // childhoodProbingQuestions: [
  //   "Thinking back to your childhood, how did you get on with other children?",
  //   "As a child, how did you keep your friendships going?",
  //   "When you were a child, what did you think a 'friend' was?",
  // ],
};

// ---------------------------------------------------------------------------
// Criterion B1 — Stereotyped or repetitive behaviour / speech
// ---------------------------------------------------------------------------

const CRITERION_B1 = {
  id: 'B1',
  criterionGroup: 'B',
  shortTitle: 'Stereotyped behaviour/speech',
  fullTitle: 'B1. Stereotyped or repetitive motor movements, use of objects, or speech',
  description:
    'This criterion explores repetitive motor behaviours, unusual use of objects, ' +
    'or idiosyncratic patterns of language that may have been present since childhood.',
  available: true,

  // Categorised questions for The Owl Centre. Three thematic groups covering
  // repetitive/formal speech, physical mannerisms, and repetitive play patterns.
  categorisedProbingQuestions: [
    {
      category: 'Repetitive speech, formal & stereotyped',
      questions: [
        'Has anyone said that you use formal language or big words or sound posh?',
        'When you were a child did anyone ever say that you spoke like an adult or mini professor?',
        'Do you often find yourself repeating words and phrases said by others?',
        'Do you ever take on the accents or mannerisms of those you are talking to?',
        'Do you have any favourite words that you enjoy saying for their sound or shape?',
        'Do you ever find that you pick up words or phrases from films or TV shows that you repeat?',
        'Do you have any set words or phrases that are your go-to when you are having a conversation, like a script?',
        'If someone interrupted you when you were talking would you continue or start again at the beginning? Would you be upset by this?',
      ],
    },
    {
      category: 'Mannerisms',
      questions: [
        'Do you do any actions with your body such as hand flapping, clapping, twisting or flicking your fingers, flicking your fingers back and forth in front of your face, clicking your fingers or joints, rocking back and forth on your feet or when seated, toe walking, spinning around in circles, tensing your jaw or grinding your teeth, or repeatedly clearing your throat?',
      ],
    },
    {
      category: 'Repetitive play',
      questions: [
        'When you were a child did you ever line up your toys?',
        'Would you play with certain parts of a toy like spinning the wheels of cars repeatedly?',
        'Did you ever take your toys apart or do experiments with them like dropping them from the same distance, banging or shaking them?',
        'Did you ever do things repetitively like turning the light on and off or closing and opening doors?',
        'Did you ever collect objects like stones or have sets of certain toys?',
        'Do you have any music or videos that you listen to or watch repeatedly?',
        'Have your childhood interests changed over time or are they exactly the same as an adult?',
      ],
    },
  ],

  // Legacy life-stage-split questions — kept for potential reuse by other
  // organisations that prefer the Childhood / Adulthood accordion layout.
  // adulthoodProbingQuestions: [
  //   "What does your body do when you're feeling really happy, excited, stressed, or focused? Do you ever rock, flap your hands, pace, or tap?",
  //   "Has anyone ever said you have a unique way of speaking like a distinctive rhythm, tone, or way of telling stories?",
  //   "Do you find yourself repeating phrases, quotes, or lines from media, or perhaps echoing words or sentences again and again?",
  // ],
  // childhoodProbingQuestions: [
  //   "What did you do with your body when you were a happy or excited kid? Did you jump, spin, or flap your hands?",
  //   "As a child, how did you typically play with toys?",
  //   "Some children repeat words, phrases, or lines from shows as a way to process or express themselves. Was that ever true for you?",
  // ],
};

// ---------------------------------------------------------------------------
// Criterion B2 — Insistence on sameness / routines
// ---------------------------------------------------------------------------

const CRITERION_B2 = {
  id: 'B2',
  criterionGroup: 'B',
  shortTitle: 'Insistence on sameness',
  fullTitle: 'B2. Insistence on sameness, inflexible adherence to routines, or ritualised patterns',
  description:
    'This criterion explores a person\'s need for predictability, routine, and ' +
    'control over daily activities or communication, and their level of distress ' +
    'or disruption when these are changed.',
  available: true,

  // Categorised questions for The Owl Centre. Four thematic groups covering
  // imaginative play, change management, routines/rituals, and rigid thinking.
  // Each category renders as a collapsible accordion in AdultAsdCriterionPanel.
  categorisedProbingQuestions: [
    {
      category: 'Imaginative play',
      questions: [
        'What did/do you like to play with?',
        'Did you play any pretend games with a story line?',
        'If you try to imagine something, do you find it easy to create a picture in your mind?',
      ],
    },
    {
      category: 'Managing change',
      questions: [
        'Do you tend to notice small details and changes about people\'s appearance?',
        'Do you have difficulties moving between tasks?',
        'Do you like things to be the same and predictable?',
        'Do you take the same routes when you are walking or driving?',
        'Do you arrange objects in the same way each time?',
        'How would you feel if someone moved your things?',
        'How do you feel if plans are cancelled or changed at short notice?',
        'Would you be okay with someone suggesting you do something at short notice?',
        'Did you struggle to transition between classes at school?',
        'What about school trips, or if there was a supply teacher or non-uniform day?',
        'Would the transition between school day and weekend, summer holiday and return to school be a problem for you?',
        'Did you ever go on holiday as a child? Was this something you enjoyed or found difficult?',
      ],
    },
    {
      category: 'Routines & rituals',
      questions: [
        'Do you have routines where you need to do things in the same way every time?',
        'Would you say that you have your house organised with everything in a particular place?',
        'Do you have difficulties moving between tasks, if you have to do something else when you\'re in the middle of something?',
        'Do you have any compulsions like touching particular things or putting things in certain places before you move on to the next task?',
        'How would you feel if you were unable to complete the whole sequence of this activity or were disrupted?',
        'Have you ever been described as a rule follower or a perfectionist?',
      ],
    },
    {
      category: 'Rigid thinking',
      questions: [
        'Do you tend to hold grudges? Has this impacted your friendships?',
        'Do you have a particular eye for detail? Are you able to see the bigger picture or do you tend to focus on small details?',
        'Do you notice patterns in things, e.g. car number plates or similar strings of information?',
      ],
    },
  ],

  // Legacy life-stage-split questions — kept for potential reuse by other
  // organisations that prefer the Childhood / Adulthood accordion layout.
  // adulthoodProbingQuestions: [
  //   "Do you have any habits or ways of doing things that are really important to you?",
  //   "How do you feel about surprises or last-minute changes?",
  // ],
  // childhoodProbingQuestions: [
  //   "What routines or habits were really important to you as a child?",
  // ],
};

// ---------------------------------------------------------------------------
// Criterion B3 — Restricted interests
// ---------------------------------------------------------------------------

const CRITERION_B3 = {
  id: 'B3',
  criterionGroup: 'B',
  shortTitle: 'Restricted interests',
  fullTitle: 'B3. Highly restricted, fixated interests that are abnormal in intensity or focus',
  description:
    'This criterion explores intense, passionate interests that are narrow in focus. ' +
    'These interests are pursued with exceptional dedication and are a primary source ' +
    'of joy, regulation, and identity. They are distinct from casual hobbies in their ' +
    'all-consuming nature and internal drive.',
  available: true,

  // Life-stage-agnostic questions for The Owl Centre. Cover restricted/fixated
  // interests, fact-collecting, object attachments, and exceptional abilities.
  probingQuestions: [
    'Do you have any hobbies or collections?',
    'Would you say that any of these are very intense or all consuming?',
    'Do you spend time reading about and researching your hobbies or interests or collecting facts about them?',
    'Do you have any strong attachments to inanimate objects that you have to carry around or hold?',
    'Do you have any strong interests in numbers, letters, symbols, dates or historical events?',
    'Do you have any special skills or can you do anything that other people can\'t do, e.g. have a photographic memory or anything like that?',
  ],

  // Legacy life-stage-split questions — kept for potential reuse by other
  // organisations that prefer the Childhood / Adulthood accordion layout.
  // adulthoodProbingQuestions: [
  //   "Are there topics, hobbies, or activities you feel deeply passionate about and spend a lot of time on?",
  //   "How do these passions fit into your life? Have they influenced your learning, work, or how you connect with others?",
  //   "How easy or difficult is it for you to switch away from your interests to talk about other things, or to do other tasks that are required of you?",
  // ],
  // childhoodProbingQuestions: [
  //   "Did you have interests that felt especially important or absorbing in your childhood, even if others didn't understand them?",
  // ],
};

// ---------------------------------------------------------------------------
// Criterion B4 — Sensory reactivity
// ---------------------------------------------------------------------------

const CRITERION_B4 = {
  id: 'B4',
  criterionGroup: 'B',
  shortTitle: 'Sensory reactivity',
  fullTitle: 'B4. Hyper- or hyporeactivity to sensory input or unusual sensory interests',
  description:
    'This criterion explores differences in how individuals perceive, process, and ' +
    'respond to sensory input including hypersensitivity (over-reactivity), ' +
    'hyposensitivity (under-reactivity), and atypical sensory interests or seeking ' +
    'behaviours. These may affect one or more senses (e.g., sound, touch, light, ' +
    'smell, taste, proprioception) and can be distressing, soothing, or both.',
  available: true,

  // Life-stage-agnostic questions for The Owl Centre. These cover sensory
  // sensitivities, sensory interests, interoception, and pain threshold across
  // the lifespan without splitting childhood vs. adulthood explicitly.
  probingQuestions: [
    'Would you say that you have any sensory sensitivities, such as dislike of bright lights, noticing smells or noises others don\'t, struggling with certain food or clothing textures for example?',
    'What were you like as a child with things like having your hair and teeth brushed or attending the dentist or hairdresser?',
    'Do you have any sensory interests, such as spinning or clicking things, looking at shiny, glittery or reflective objects, needing to touch certain objects or textures or repeatedly sniffing certain smells?',
    'Would you say that you have a high or low tolerance for pain?',
    'Can you easily tell when you are hungry or need the toilet or are feeling hot and cold?',
    'Do you enjoy going on swings and rollercoasters?',
  ],

  // Legacy life-stage-split questions — kept for potential reuse by other
  // organisations that prefer the Childhood / Adulthood accordion layout.
  // adulthoodProbingQuestions: [
  //   "Let's talk about your senses. Are there any sounds, lights, smells, or textures that are really overwhelming or painful for you?",
  //   "On the other hand, are there any sensory experiences you really love or seek out like certain sounds, lights, textures, pressures, or movements?",
  //   "How aware are you of signals from your body, like hunger, thirst, tiredness, or pain? Do you notice them easily, or does it sometimes take a while?",
  // ],
  // childhoodProbingQuestions: [
  //   "What were your sensory experiences like as a child? For example, were you a picky eater? Did you react strongly to loud noises like vacuum cleaners or to having your hair washed or nails cut?",
  // ],
};

// ---------------------------------------------------------------------------
// Functional Impairment domains
// ---------------------------------------------------------------------------
//
// Note: there is no standalone functional-impairment panel in the Adult ASD
// SSI. Impairment is captured within each criterion page via the per-criterion
// "Domains Impacted" chips, which are sourced from the domain lists below.

/**
 * Seven functional impairment domains for the adult assessment. Adds
 * "Occupational" compared to the CYP version (which has 6 domains) because
 * adult service users may be in employment. "Educational" is retained because
 * some adults are still in education (university, further education, etc.).
 *
 * Indicators are adult-appropriate adaptations of the CYP equivalents,
 * reflecting the lived experience of autistic adults rather than children.
 */
export const FUNCTIONAL_IMPAIRMENT_DOMAINS = [
  {
    id: 'social',
    title: 'Social',
    indicators: [
      'Struggles to make or keep friends',
      'Friendships are often short-lived or end due to misunderstandings',
      'Difficulty sharing feelings or connecting emotionally with others',
      'Social isolation or withdrawal from social activities',
      'Misunderstandings leading to conflict or relationship breakdown',
      'Strain in romantic or family relationships',
      'Relationships based primarily on shared interests rather than emotional bond',
      'Difficulty with social boundaries or reading social situations',
      'Avoids social events due to anxiety or overwhelm',
      'Relies heavily on a partner, family member, or friend to navigate social situations',
    ],
  },
  {
    id: 'educational',
    title: 'Educational',
    indicators: [
      'Difficulty participating in group discussions or collaborative work',
      'Anxiety about attending lectures, seminars, or tutorials',
      'Struggles with unstructured study time or open-ended assignments',
      'Difficulty understanding implicit expectations from tutors or peers',
      'Sensory overload in educational environments (lecture halls, libraries)',
      'Reduced attendance or avoidance of educational settings',
      'Interest-driven learning — excels in preferred subjects, struggles in others',
      'Difficulty navigating social aspects of educational environments',
    ],
  },
  {
    id: 'occupational',
    title: 'Occupational',
    indicators: [
      'Difficulty with workplace social dynamics (small talk, team meetings, networking)',
      'Struggles with unwritten workplace rules or expectations',
      'Difficulty managing changes to work routines or responsibilities',
      'Conflict or misunderstandings with colleagues or managers',
      'Sensory overload in the workplace (open-plan offices, noise, lighting)',
      'Burnout or exhaustion from masking at work',
      'Difficulty with job interviews or workplace transitions',
      'Underemployment relative to qualifications or ability',
      'Difficulty balancing work demands with personal needs',
      'Restricted interests influence career path or limit flexibility',
    ],
  },
  {
    id: 'home',
    title: 'Home',
    indicators: [
      'Difficulty managing household responsibilities without structure or prompting',
      'Rigid routines causing tension with household members',
      'Emotional outbursts or shutdowns during disruptions to routine',
      'Family or partner adapting daily life to avoid triggers',
      'Conflict around expectations of daily living tasks',
      'Difficulty with multi-step domestic tasks (cooking, cleaning, laundry)',
      'Dependence on others for organisation or daily planning',
      'Interest interferes with household responsibilities',
    ],
  },
  {
    id: 'emotional',
    title: 'Emotional',
    indicators: [
      'High anxiety around social situations or unpredictability',
      'Emotional exhaustion after social interaction or masking',
      'Shutdowns, meltdowns, withdrawal, or avoidance',
      'Low self-esteem from repeated social difficulties or feeling different',
      'Difficulty identifying or expressing emotions (alexithymia)',
      'Experiences significant loneliness, anxiety, or depression',
      'Anxiety about future due to unpredictability',
      'History of mental health difficulties that may relate to unrecognised autism',
    ],
  },
  {
    id: 'community',
    title: 'Community',
    indicators: [
      'Avoids community activities, clubs, or group settings',
      'Overwhelm or distress in busy or noisy environments (shops, transport, events)',
      'Difficulty following unspoken social rules in community settings',
      'Limited participation in leisure or recreational activities',
      'Difficulty using public transport independently',
      'Needs accompaniment or support to access community settings',
    ],
  },
  {
    id: 'selfCare',
    title: 'Self-care',
    indicators: [
      'Difficulty maintaining consistent sleep routines',
      'Challenges with personal hygiene without prompting or routine',
      'Restricted eating linked to sensory sensitivities',
      'Difficulty managing finances, appointments, or admin tasks',
      'Neglects health needs (forgets medication, avoids appointments)',
      'Difficulty recognising and responding to internal body signals (hunger, thirst, pain)',
      'Interest interferes with sleep or self-care',
      'Overeats or undereats due to difficulty sensing hunger or fullness',
    ],
  },
];

/**
 * Childhood-eligible domains — excludes Occupational because children are
 * not in employment. The Occupational domain is only relevant to Adulthood.
 */
export const CHILDHOOD_DOMAINS = FUNCTIONAL_IMPAIRMENT_DOMAINS.filter(
  d => d.id !== 'occupational'
);

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * All DSM-5 ASD criteria in assessment order.
 * Seven criteria (A1-A3, B1-B4), each with adulthood and childhood
 * probing questions for the adult semi-structured interview.
 */
export const SSI_CRITERIA = [
  CRITERION_A1,
  CRITERION_A2,
  CRITERION_A3,
  CRITERION_B1,
  CRITERION_B2,
  CRITERION_B3,
  CRITERION_B4,
];

/**
 * Returns every probing question string defined for a criterion, regardless of
 * whether the config uses flat, categorised, or legacy life-stage-split lists.
 * Used by demo-data validation and tests to ensure persisted asked_questions
 * stay aligned with the live SSI reference panel.
 */
export function getAllProbingQuestionsForCriterion(criterion) {
  if (criterion.categorisedProbingQuestions) {
    return criterion.categorisedProbingQuestions.flatMap(({ questions }) => questions);
  }
  if (criterion.probingQuestions) {
    return criterion.probingQuestions;
  }
  return [
    ...(criterion.adulthoodProbingQuestions || []),
    ...(criterion.childhoodProbingQuestions || []),
  ];
}

/**
 * Returns true when at least one DSM-5 criterion in the SSI criteria data
 * contains recorded evidence, notes, or domain selections -- the three
 * field families that the backend extractor (extract_criteria_inputs in
 * llm_inputs_asd_ssi.py) actually reads. asked_questions is excluded
 * because it is a UI checklist of probing items the clinician marked as
 * covered, not a recorded interview answer -- the backend ignores it.
 *
 * Used as a frontend guard to prevent TriggerGenerateDocumentSection from
 * firing with empty criteria data, which would cause the Cloud Function to
 * abort with a 500 (the if-not-llm_inputs_raw check in main.py).
 *
 * @param {Object|undefined|null} criteriaData - The criteria object from
 *   assessmentStages.semiStructuredInterview.data.criteria (the Firestore
 *   snapshot) or the local optimistic allData state. Both share the same
 *   shape: keyed by criterion ID (A1-B4), each holding evidence ratings,
 *   notes arrays, domain arrays, and asked_questions arrays.
 * @returns {boolean} true if any criterion has at least one piece of
 *   evidence, a note, or a domain selection recorded.
 */
export function hasAsdSsiCriteriaInput(criteriaData) {
  if (!criteriaData || typeof criteriaData !== 'object') return false;
  return Object.values(criteriaData).some(criterion => {
    if (!criterion || typeof criterion !== 'object') return false;
    // These six fields mirror extract_criteria_inputs exactly:
    // evidence_{childhood,adulthood}, notes_{childhood,adulthood},
    // domains_{childhood,adulthood}.
    return Boolean(criterion.evidence_childhood)
      || Boolean(criterion.evidence_adulthood)
      || (Array.isArray(criterion.notes_childhood) && criterion.notes_childhood.length > 0)
      || (Array.isArray(criterion.notes_adulthood) && criterion.notes_adulthood.length > 0)
      || (Array.isArray(criterion.domains_childhood) && criterion.domains_childhood.length > 0)
      || (Array.isArray(criterion.domains_adulthood) && criterion.domains_adulthood.length > 0);
  });
}

/** Scoring guidance displayed in the tooltip for each criterion group */
export const SCORING_NOTES = {
  A: 'Criterion A has three sub-criteria (A1, A2, A3). All three must be met.',
  B: 'At least two of four sub-criteria (B1\u2013B4) must be met.',
};

export default SSI_CRITERIA;
